import React from 'react';
import styled from 'styled-components';
import { RotateCcw, Download, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../../styles/ThemeStyles';
import { FormStatus } from '../../../services/FormCacheService';

interface FormStatusIndicatorProps {
  formType: 'heritage' | 'livingston' | 'flower' | 'vegetable';
  status: FormStatus;
  onReset: () => void;
  onLoadDraft?: () => void;
  className?: string;
}

const StatusBadge = styled.div<{ status: FormStatus['status'] }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.lg};
  border: 2px solid;
  background: ${props => {
    switch (props.status) {
      case 'empty':
        return ThemeColors.background.paper;
      case 'in_progress':
        return 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)';
      case 'completed':
        return 'linear-gradient(135deg, #d4edda 0%, #a8e6cf 100%)';
      case 'error':
        return 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)';
      default:
        return ThemeColors.background.paper;
    }
  }};
  border-color: ${props => {
    switch (props.status) {
      case 'empty':
        return ThemeColors.border.light;
      case 'in_progress':
        return '#ffc107';
      case 'completed':
        return ThemeColors.success.main;
      case 'error':
        return ThemeColors.error.main;
      default:
        return ThemeColors.border.light;
    }
  }};
  box-shadow: ${Shadows.sm};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
  }
`;

const StatusIcon = styled.div<{ status: FormStatus['status'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'empty':
        return ThemeColors.background.default;
      case 'in_progress':
        return '#ffc107';
      case 'completed':
        return ThemeColors.success.main;
      case 'error':
        return ThemeColors.error.main;
      default:
        return ThemeColors.background.default;
    }
  }};
  color: white;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const StatusTitle = styled.div<{ status: FormStatus['status'] }>`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${props => {
    switch (props.status) {
      case 'empty':
        return ThemeColors.text.secondary;
      case 'in_progress':
        return '#856404';
      case 'completed':
        return ThemeColors.success.dark;
      case 'error':
        return ThemeColors.error.dark;
      default:
        return ThemeColors.text.secondary;
    }
  }};
  text-transform: capitalize;
  margin-bottom: 2px;
`;

const StatusDetails = styled.div<{ status: FormStatus['status'] }>`
  font-size: ${Typography.fontSize.xs};
  color: ${props => {
    switch (props.status) {
      case 'empty':
        return ThemeColors.text.secondary;
      case 'in_progress':
        return '#856404';
      case 'completed':
        return ThemeColors.success.dark;
      case 'error':
        return ThemeColors.error.dark;
      default:
        return ThemeColors.text.secondary;
    }
  }};
  opacity: 0.8;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  align-items: center;
`;

const ActionButton = styled.button<{ variant: 'reset' | 'load' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${BorderRadius.sm};
  background: ${props => props.variant === 'reset' ? ThemeColors.error.light : ThemeColors.primary.light};
  color: ${props => props.variant === 'reset' ? ThemeColors.error.main : ThemeColors.primary.main};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'reset' ? ThemeColors.error.main : ThemeColors.primary.main};
    color: white;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const getStatusIcon = (status: FormStatus['status']) => {
  switch (status) {
    case 'empty':
      return <Clock size={14} />;
    case 'in_progress':
      return <Clock size={14} />;
    case 'completed':
      return <CheckCircle size={14} />;
    case 'error':
      return <AlertCircle size={14} />;
    default:
      return <Clock size={14} />;
  }
};

const getStatusText = (status: FormStatus['status'], itemCount: number, totalAmount: number) => {
  // Debug logging to track status indicator values
  console.log(`FormStatusIndicator - status: ${status}, itemCount: ${itemCount}, totalAmount: ${totalAmount}`);
  
  switch (status) {
    case 'empty':
      return 'No items selected';
    case 'in_progress':
      return `${itemCount} item${itemCount !== 1 ? 's' : ''} - $${totalAmount.toFixed(2)}`;
    case 'completed':
      return `Ready to submit (${itemCount} items)`;
    case 'error':
      return 'Has errors - check form';
    default:
      return 'Unknown status';
  }
};

export const FormStatusIndicator: React.FC<FormStatusIndicatorProps> = ({
  formType,
  status,
  onReset,
  onLoadDraft,
  className
}) => {
  const handleReset = () => {
    if (window.confirm(`Are you sure you want to reset the ${formType} form? This will clear all selected items.`)) {
      onReset();
    }
  };

  const handleLoadDraft = () => {
    if (onLoadDraft) {
      onLoadDraft();
    }
  };

  return (
    <StatusBadge status={status.status} className={className}>
      <StatusIcon status={status.status}>
        {getStatusIcon(status.status)}
      </StatusIcon>
      
      <StatusContent>
        <StatusTitle status={status.status}>
          {formType} Order Form
        </StatusTitle>
        <StatusDetails status={status.status}>
          {getStatusText(status.status, status.itemCount, status.totalAmount)}
        </StatusDetails>
      </StatusContent>
      
      <ActionButtons>
        {status.status !== 'empty' && (
          <ActionButton
            variant="reset"
            onClick={handleReset}
            title={`Reset ${formType} form`}
            aria-label={`Reset ${formType} form`}
          >
            <RotateCcw size={14} />
          </ActionButton>
        )}
        
        {status.status === 'in_progress' && onLoadDraft && (
          <ActionButton
            variant="load"
            onClick={handleLoadDraft}
            title={`Load ${formType} draft`}
            aria-label={`Load ${formType} draft`}
          >
            <Download size={14} />
          </ActionButton>
        )}
      </ActionButtons>
    </StatusBadge>
  );
};

export default FormStatusIndicator;
